// @ts-check
import { z } from "zod"
import { FileText, Pencil, RefreshCw } from "lucide-react"
import ToolCall from "@/components/tool-call"

// ─── Schemas ─────────────────────────────────────────────────────────────────

const CreateCanvasArgsSchema = z.object({
  title: z.string(),
  content: z.string(),
}).passthrough()

const CreateCanvasResultSchema = z.object({
  document: z.object({ _id: z.string(), title: z.string() }).passthrough(),
}).passthrough()

const EditDocumentArgsSchema = z.object({
  documentId: z.string(),
  edits: z.array(z.object({
    id: z.string(),
    old_string: z.string(),
    new_string: z.string(),
    rationale: z.string().optional(),
  })),
}).passthrough()

const EditDocumentResultSchema = z.object({
  document: z.object({ _id: z.string(), title: z.string() }).passthrough(),
  diffIds: z.array(z.string()).optional(),
}).passthrough()

const WriteDocumentArgsSchema = z.object({
  documentId: z.string(),
  content: z.string(),
  rationale: z.string().optional(),
}).passthrough()

const WriteDocumentResultSchema = EditDocumentResultSchema

// ─── Components ──────────────────────────────────────────────────────────────

/** @param {import(".").ToolComponentProps<typeof CreateCanvasArgsSchema, typeof CreateCanvasResultSchema>} props */
function CreateCanvasUI({ args, result, state }) {
  const title = result instanceof Error ? args.title : (result?.document?.title ?? args.title)
  return (
    <ToolCall state={state}>
      <ToolCall.Header>
        <FileText size={16} className="shrink-0" />
        <ToolCall.Action>Create document</ToolCall.Action>
        <ToolCall.Input>{title}</ToolCall.Input>
      </ToolCall.Header>
    </ToolCall>
  )
}

/** @param {import(".").ToolComponentProps<typeof EditDocumentArgsSchema, typeof EditDocumentResultSchema>} props */
function EditDocumentUI({ args, result, state }) {
  const editCount = args.edits?.length ?? 0
  const isSaved = result != null && !(result instanceof Error)
  const added = isSaved ? (result.diffIds?.length ?? editCount) : editCount
  const skipped = isSaved && Array.isArray(result.skipped) ? result.skipped.length : 0
  return (
    <ToolCall state={state}>
      <ToolCall.Header>
        <Pencil size={16} className="shrink-0" />
        <ToolCall.Action>Edit document</ToolCall.Action>
        <ToolCall.Input>
          {added} suggestion{added !== 1 ? "s" : ""}{skipped > 0 ? ` · ${skipped} did not match the text` : ""}
        </ToolCall.Input>
      </ToolCall.Header>
    </ToolCall>
  )
}

/** @param {import(".").ToolComponentProps<typeof WriteDocumentArgsSchema, typeof WriteDocumentResultSchema>} props */
function WriteDocumentUI({ state }) {
  return (
    <ToolCall state={state}>
      <ToolCall.Header>
        <RefreshCw size={16} className="shrink-0" />
        <ToolCall.Action>Rewrite document</ToolCall.Action>
      </ToolCall.Header>
    </ToolCall>
  )
}

export {
  CreateCanvasArgsSchema,
  CreateCanvasResultSchema,
  CreateCanvasUI,
  EditDocumentArgsSchema,
  EditDocumentResultSchema,
  EditDocumentUI,
  WriteDocumentArgsSchema,
  WriteDocumentResultSchema,
  WriteDocumentUI,
}
